import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { Trash2, UserPlus, Users } from 'lucide-react';

import { Button, cn, Select } from '@src/components/ui';
import { ROLE_DESCRIPTIONS } from '@src/constants';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import {
  useInviteActionMutation,
  useMemberMutation,
  useOptionLabels,
  useTeamQuery,
} from '@src/query';

import { Skeleton, WidgetError } from '../Dashboard/WidgetState';
import { Toaster, useToasts } from '../Listings/useToasts';
import {
  Avatar,
  formatDate,
  fullName,
  isDowngrade,
  RoleChip,
  useAssignableRoles,
  useInvitableRoles,
  useRoleLabel,
} from './bits';
import { ConfirmDialog } from './controls';
import InviteModal from './InviteModal';
import PageHeader from './PageHeader';


/** Seat usage banner; at cap the invite affordance disables and an Upgrade CTA
 * shows (.pen Team header). */
function SeatBar({ seats, subscription, atCap }) {
  const planLabels = useOptionLabels('subscription_plan');
  const used = seats?.used ?? 0;
  const cap = seats?.cap ?? 0;
  const plan = subscription?.plan;
  const planLabel = planLabels[plan] || (plan ? `${plan[0].toUpperCase()}${plan.slice(1)}` : 'Free');

  return (
    <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-hairline bg-surface-1 px-3.5 py-3">
      <Users className="size-4 text-ink-muted" />
      <span className="text-table font-semibold text-ink">{`${used} of ${cap} seats used`}</span>
      <span className="text-table text-ink-subtle">{`${planLabel} plan`}</span>
      <span className="flex-1" />

      {atCap
        ? <span className="text-table font-medium text-primary">Upgrade for more seats →</span>
        : null}
    </div>
  );
}

SeatBar.displayName = 'SeatBar';


/** Persistent role legend (#151) — copy from the .pen Invite Modal. Owner is a
 * fixed leading row (set by transfer, never invitable); the rest are the
 * server-driven invitable roles. */
function RoleLegend() {
  const invitableRoles = useInvitableRoles();
  const rows = [{ value: 'owner', description: ROLE_DESCRIPTIONS.owner }, ...invitableRoles];
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-hairline bg-surface-1 p-4">
      <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">Roles</span>

      {rows.map(({ value, description }) => (
        <div key={value} className="flex items-start gap-3">
          <RoleChip className="mt-px w-[68px] text-center" role={value} />
          <span className="flex-1 text-table text-ink-muted">{description}</span>
        </div>
      ))}
    </div>
  );
}

RoleLegend.displayName = 'RoleLegend';


/** Inline role <select> + remove for a member row. Disabled for the Owner row
 * and the current user's own row (the API also blocks both). */
function MemberActions(props) {
  const { member, isSelf, onChangeRole, onRemove, busy } = props;
  const roleLabel = useRoleLabel();
  const assignableRoles = useAssignableRoles();
  const locked = member.role === 'owner' || isSelf;

  if (locked) {
    return (
      <span className="text-xs text-ink-tertiary">{isSelf ? 'You' : 'Owner'}</span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Select
        disabled={busy}
        className="w-auto"
        value={member.role}
        aria-label={`Role for ${fullName(member.user) || member.user?.email}`}
        onChange={(event) => onChangeRole(member, event.target.value)}
      >
        {assignableRoles.map((role) => (
          <option key={role} value={role}>{roleLabel(role)}</option>
        ))}
      </Select>

      <Button
        size="icon"
        type="button"
        variant="ghost"
        disabled={busy}
        aria-label={`Remove ${fullName(member.user) || member.user?.email}`}
        className="size-[30px] text-ink-subtle hover:bg-surface-2 hover:text-danger"
        onClick={() => onRemove(member)}
      >
        <Trash2 className="size-[15px]" />
      </Button>
    </div>
  );
}

MemberActions.displayName = 'MemberActions';


function MemberRow({ member, isSelf, last, onChangeRole, onRemove, busy }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3',
        !last && 'border-b border-hairline',
      )}
    >
      <Avatar user={member.user} />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-ink">
          {fullName(member.user) || member.user?.email}
        </span>

        <span className="truncate text-xs text-ink-subtle">{member.user?.email}</span>
      </span>

      <RoleChip role={member.role} />

      <MemberActions
        busy={busy}
        member={member}
        isSelf={isSelf}
        onRemove={onRemove}
        onChangeRole={onChangeRole}
      />
    </div>
  );
}

MemberRow.displayName = 'MemberRow';


function InviteRow({ invite, onResend, onRevoke, busy }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-1 px-4 py-3">
      <Avatar muted size={34} />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-ink">{invite.email}</span>

        <span className="truncate text-xs text-ink-subtle">
          {`Invited ${formatDate(invite.created_at)}`}
          {invite.invited_by_name ? ` by ${invite.invited_by_name}` : ''}
        </span>
      </span>

      <RoleChip role={invite.role} />

      <Button
        type="button"
        disabled={busy}
        variant="secondary"
        className="h-[30px] px-3 text-table"
        onClick={() => onResend(invite)}
      >
        Resend
      </Button>

      <Button
        size="icon"
        type="button"
        variant="ghost"
        disabled={busy}
        aria-label={`Revoke invite for ${invite.email}`}
        className="size-[30px] text-ink-subtle hover:text-danger"
        onClick={() => onRevoke(invite)}
      >
        <Trash2 className="size-[15px]" />
      </Button>
    </div>
  );
}

InviteRow.displayName = 'InviteRow';


/** Team page (#148–#153, .pen D7QfTy): seats header, member roster with role
 * change / removal, pending invites with resend / revoke, role legend, invite
 * modal. Owner/Manager only; other roles redirect to the hub. */
function Team() {
  const { toasts, push } = useToasts();
  const roleLabel = useRoleLabel();
  const { membership, seller } = useActiveOrg();
  const { canManageOrg: canManage } = usePermissions();
  const selfMembershipId = membership?.id;

  const [showInvite, setShowInvite] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const teamQuery = useTeamQuery(seller?.id, { enabled: Boolean(seller) && canManage });

  const memberMutation = useMemberMutation();
  const inviteActionMutation = useInviteActionMutation();

  if (!seller) {
    return null;
  }

  if (!canManage) {
    return <Navigate replace to="/app/settings" />;
  }

  const data = teamQuery.data;
  const members = data?.members || [];
  const invitations = (data?.invitations || []).filter((invite) => invite.status === 'pending');
  const seats = data?.seats;
  const atCap = Boolean(seats) && seats.used >= seats.cap;
  const busy = memberMutation.isPending || inviteActionMutation.isPending;

  const refresh = () => teamQuery.refetch();

  const applyRoleChange = (member, nextRole) => {
    memberMutation.mutate(
      { sellerId: seller.id, method: 'PATCH', membershipId: member.id, payload: { role: nextRole } },
      {
        onSuccess: () => {
          push('Role updated');
          refresh();
        },
        onError: (error) => push(error?.response?.data?.detail || 'Could not change role', 'danger'),
      },
    );
  };

  const onChangeRole = (member, nextRole) => {
    if (nextRole === member.role) {
      return;
    }
    if (isDowngrade(member.role, nextRole)) {
      setConfirm({
        kind: 'downgrade',
        member,
        nextRole,
        title: `Change ${fullName(member.user) || member.user?.email} to ${roleLabel(nextRole)}?`,
        body: 'This reduces their access immediately.',
        confirmLabel: 'Change role',
      });
      return;
    }
    applyRoleChange(member, nextRole);
  };

  const onRemove = (member) => {
    setConfirm({
      kind: 'remove',
      member,
      title: `Remove ${fullName(member.user) || member.user?.email}?`,
      body: 'They lose access to this organization immediately.',
      confirmLabel: 'Remove member',
      danger: true,
    });
  };

  const runConfirm = () => {
    const pending = confirm;
    setConfirm(null);
    if (pending.kind === 'remove') {
      memberMutation.mutate(
        { sellerId: seller.id, method: 'DELETE', membershipId: pending.member.id },
        {
          onSuccess: () => {
            push('Member removed');
            refresh();
          },
          onError: (error) => push(error?.response?.data?.detail || 'Could not remove member', 'danger'),
        },
      );
    }
    else {
      applyRoleChange(pending.member, pending.nextRole);
    }
  };

  const onResend = (invite) => {
    inviteActionMutation.mutate(
      { sellerId: seller.id, invitationId: invite.id, action: 'resend' },
      {
        onSuccess: () => push('Invite resent'),
        onError: () => push('Could not resend invite', 'danger'),
      },
    );
  };

  const onRevoke = (invite) => {
    inviteActionMutation.mutate(
      { sellerId: seller.id, invitationId: invite.id, action: 'revoke' },
      {
        onSuccess: () => {
          push('Invite revoked');
          refresh();
        },
        onError: () => push('Could not revoke invite', 'danger'),
      },
    );
  };

  const inviteButton = (
    <Button
      type="button"
      disabled={atCap}
      className="h-10 gap-1.5 text-sm"
      title={atCap ? 'All seats are in use — upgrade for more' : undefined}
      onClick={() => setShowInvite(true)}
    >
      <UserPlus className="size-4" />
      Invite member
    </Button>
  );

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader
        title="Team"
        subtitle={`Manage who can access ${seller.name}.`}
        actions={<span className="hidden lg:block">{inviteButton}</span>}
      />

      <div className="flex w-full max-w-[760px] flex-col gap-4 px-4 pb-6 lg:px-0">
        {/* Mobile invite button */}
        <div className="lg:hidden">{inviteButton}</div>
        <SeatBar seats={seats} atCap={atCap} subscription={seller.subscription} />

        {teamQuery.isPending
          ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )
          : teamQuery.isError
            ? <WidgetError onRetry={refresh} />
            : (
              <>
                <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Members
                </span>

                <div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
                  {members.map((member, index) => (
                    <MemberRow
                      busy={busy}
                      key={member.id}
                      member={member}
                      last={index === members.length - 1}
                      isSelf={member.id === selfMembershipId}
                      onRemove={onRemove}
                      onChangeRole={onChangeRole}
                    />
                  ))}
                </div>

                {invitations.length > 0
                  ? (
                    <>
                      <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                        Pending invites
                      </span>

                      <div className="flex flex-col gap-2.5">
                        {invitations.map((invite) => (
                          <InviteRow
                            busy={busy}
                            key={invite.id}
                            invite={invite}
                            onResend={onResend}
                            onRevoke={onRevoke}
                          />
                        ))}
                      </div>
                    </>
                  )
                  : null}

                <RoleLegend />
              </>
            )}
      </div>

      {showInvite
        ? (
          <InviteModal
            atCap={atCap}
            seller={seller}
            onToast={push}
            onInvited={refresh}
            onClose={() => setShowInvite(false)}
          />
        )
        : null}

      {confirm
        ? (
          <ConfirmDialog
            busy={busy}
            title={confirm.title}
            variant={confirm.danger ? 'danger' : 'primary'}
            body={confirm.body}
            onConfirm={runConfirm}
            onCancel={() => setConfirm(null)}
            confirmLabel={confirm.confirmLabel}
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}


Team.displayName = 'Team';
export default Team;
